import { format, setMonth as setMonthForDate, setYear as setYearForDate } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CaptionLabelProps, DayPicker } from 'react-day-picker'

import { Button } from 'core-components/library/button'
import { DropdownArrow } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'

import { DEFAULT_DATE_MASK, MONTHS } from '../constants'

import { getCurrentValue } from './helpers/getCurrentValue'
import { validateRange } from './helpers/validateRange'
import { ICalendar } from './models/ICalendar'
import { MonthList } from './MonthList'
import { TimeSlider } from './TimeSlider'
import { YearList } from './YearList'

import styles from './Calendar.module.scss'
import 'react-day-picker/dist/style.css'

export const Calendar = ({
  value,
  mode = 'single',
  onChange,
  defaultMonth,
  required,
  min,
  max,
  fromDate,
  toDate,
  minutesStep,
  onTodayButtonClick,
  beforeCalendarTemplate,
}: ICalendar) => {
  const today = useMemo(() => new Date() as Date, [])
  const isValidTodayButton = useMemo(() => {
    if (mode === 'single') {
      return validateRange(today as Date, fromDate, toDate)
    }
    return true
  }, [fromDate, mode, toDate, today])

  const [month, setMonth] = useState<Date>(defaultMonth || today)
  const [monthFilter, setMonthFilter] = useState<string>()
  const [yearFilter, setYearFilter] = useState<string>()
  const [currentView, setCurrentView] = useState<'calendar' | 'months' | 'years'>('calendar')
  const monthInputRef = useRef<HTMLInputElement>(null)
  const yearInputRef = useRef<HTMLInputElement>(null)

  const timeSliderVisible = mode === 'single' && minutesStep
  const pickerProps = {
    mode,
    selected: value,
    onSelect: onChange,
    required,
    defaultMonth,
    min,
    max,
    fromDate,
    toDate,
  }

  const classNames = {
    day: styles.day,
    day_outside: styles.day_outside,
    head_cell: styles.headCell,
    nav_button: styles.navButton,
    nav: styles.caption__nav,
    caption: styles.caption,
    today: styles.today,
  }

  const modifierClassNames = {
    selected: styles.day_selected,
    range_start: styles.range_start,
    range_middle: styles.range_middle,
    range_end: styles.range_end,
    today: styles.today,
  }

  useEffect(() => {
    const currentValue = getCurrentValue(value, defaultMonth)
    setMonth(currentValue)
  }, [value, defaultMonth])

  useEffect(() => {
    const monthIndex = month.getMonth()
    setMonthFilter(MONTHS[monthIndex])
    setYearFilter(month.getFullYear() + '')
    setCurrentView('calendar')
  }, [month])

  useEffect(() => {
    if (currentView === 'months') {
      monthInputRef.current?.focus()
    }
  }, [currentView, monthInputRef, monthFilter])

  useEffect(() => {
    if (currentView === 'years') {
      yearInputRef.current?.focus()
    }
  }, [currentView, yearInputRef, yearFilter])

  const CaptionTemplate = useCallback(
    ({ displayMonth }: CaptionLabelProps) => {
      const monthName = format(displayMonth, 'LLLL')
      const year = format(displayMonth, 'uuuu')

      return (
        <div className={styles.caption__body_wrapper}>
          <div className={styles.caption__body}>
            <Input
              ref={monthInputRef}
              classes={{ root: styles.caption__monthInput }}
              value={monthFilter}
              onChange={setMonthFilter}
              onFocus={() => setCurrentView('months')}
              __testId={'calendar__start-input'}
              endContent={
                <DropdownArrow
                  state={'down'}
                  onMouseDown={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    setCurrentView(currentView === 'months' ? 'calendar' : 'months')
                    setMonthFilter(monthName)
                  }}
                />
              }
            />
            <Input
              ref={yearInputRef}
              classes={{ root: styles.caption__yearInput }}
              value={yearFilter}
              type={'number'}
              onFocus={() => setCurrentView('years')}
              __testId={'calendar__end-input'}
              onChange={(newValue) => {
                setYearFilter(newValue.length > 4 ? '9999' : newValue)
              }}
              endContent={
                <DropdownArrow
                  state={'down'}
                  onMouseDown={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    setCurrentView(currentView === 'years' ? 'calendar' : 'years')
                    setYearFilter(year)
                  }}
                />
              }
            />
          </div>

          {beforeCalendarTemplate ? beforeCalendarTemplate({ currentMonth: month }) : null}
        </div>
      )
    },
    [beforeCalendarTemplate, currentView, month, monthFilter, yearFilter]
  )

  const renderTimeSlider = () => {
    if (timeSliderVisible) {
      return (
        <div className={styles.timeSliders}>
          <TimeSlider
            step={60}
            type={'hours'}
            value={value as Date}
            onChange={(newValue) => {
              // @ts-ignore
              onChange(newValue)
            }}
            fromDate={fromDate}
            toDate={toDate}
          />
          <span className={styles.timeSliders__splitter}>:</span>
          <TimeSlider
            step={minutesStep}
            type={'minutes'}
            value={value as Date}
            onChange={(newValue) => {
              // @ts-ignore
              onChange(newValue)
            }}
            fromDate={fromDate}
            toDate={toDate}
          />
        </div>
      )
    }
    return null
  }

  const componentsRenderFunctions = {
    CaptionLabel: CaptionTemplate,
    IconRight: () => <i className={'kbase kbase-omni-chevron-right'} />,
    IconLeft: () => <i className={'kbase kbase-omni-chevron-left'} />,
  }

  if (currentView !== 'calendar') {
    componentsRenderFunctions['Row'] = () => {
      return null
    }
  }

  return (
    <div className={styles.root}>
      {renderTimeSlider()}
      {/* @ts-ignore */}
      <DayPicker
        {...pickerProps}
        className={styles.dayPicker}
        month={month}
        onMonthChange={setMonth}
        components={componentsRenderFunctions}
        classNames={classNames}
        modifiersClassNames={modifierClassNames}
        weekStartsOn={1}
        showOutsideDays
      />
      {currentView === 'months' ? (
        <MonthList
          selectMonth={format(month, 'LLLL')}
          filter={monthFilter || ''}
          onSelectMonthChanged={(newMonth) => {
            const monthIndex = MONTHS.indexOf(newMonth)
            const newValue = setMonthForDate(month as Date, monthIndex)
            setMonth(newValue)
          }}
        />
      ) : null}

      {currentView === 'years' ? (
        <YearList
          selectYear={format(month, 'yyyy')}
          filter={yearFilter || ''}
          onSelectYearChanged={(newYear) => {
            const newValue = setYearForDate(month as Date, newYear)
            setMonth(newValue)
          }}
        />
      ) : null}

      {isValidTodayButton ? (
        <div className={styles.currentDate}>
          <Button
            __testId={'today_button'}
            icon={'kbase kbase-calendar'}
            variant={'text'}
            onClick={() => {
              setMonth(today)
              if (onTodayButtonClick) {
                onTodayButtonClick(today)
              }
            }}
          >
            Today{' '}
            <span className={'hide_to_lostpixel_test'} data-testid="calendar__today-date">
              {format(today, DEFAULT_DATE_MASK)}
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  )
}
